import { useCallback } from 'react';
import { Center } from '~/commonComponents/Center';
import CustomSpinner from '~/commonComponents/CustomSpinner';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ProfessionTypeEnum } from '~/models';
import CloseIcon from '../../images/close-icon.svg';
import Button from '../Button';
import { ClickAwayListener } from '../ClickAwayListener';
import { Column } from '../Column';
import { Pressable } from '../Pressable';
import { Tab, TabList, TabPanel, Tabs } from '../Tabs';
import { Text } from '../Text';
import {
  Container,
  FavoriteTabPanel,
  Overlay,
  RecentProfessionalTabPanel,
  RecommendedProsTabPanel,
} from './components';
import { useInviteProfessional } from './hooks';

interface IProps {
  isOpen: boolean;
  isRefetchJob: boolean;
  confirmButtonText?: string;
  jobId?: string;
  jobIds?: string[];
  defaultProfessionals?: string[];
  profession?: string;
  officeId?: string;
  onClose: () => void;
  onConfirm?: (ids: string[]) => void;
  onTrackEvent?: () => void;
}

export const InviteProfessionalPopup = ({
  isOpen,
  isRefetchJob,
  confirmButtonText = 'Send Invites',
  jobId,
  jobIds,
  defaultProfessionals,
  profession,
  officeId,
  onClose,
  onConfirm,
  onTrackEvent,
}: IProps) => {
  const {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
    isRecommendedProsEnabled,
  } = useInviteProfessional({
    isRefetchJob,
    jobId,
    jobIds,
    defaultProfessionals,
    officeId,
    isOpen,
    onClosePopup: onClose,
    filterProfession: profession as ProfessionTypeEnum,
  });

  const handleConfirmProfessionals = useCallback(() => {
    onTrackEvent?.();

    if (onConfirm) {
      onConfirm([...selectedProfessionals]);

      return;
    }

    onSentInvitations();
  }, [onConfirm, onTrackEvent, selectedProfessionals, onSentInvitations]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isSuccess ? (
        <Overlay>
          <ClickAwayListener onClickAway={() => onClosePopup()}>
            <Container>
              <Pressable onPress={onClosePopup}>
                <img
                  src={CloseIcon}
                  style={{
                    cursor: 'pointer',
                    height: 24,
                    position: 'absolute',
                    right: 20,
                    top: 20,
                    width: 24,
                  }}
                  alt="close_icon"
                />
              </Pressable>

              <Column marginBottom={24} marginTop={54}>
                <Text bold fontSize={18} textAlign="center">
                  Invite Professionals
                </Text>
              </Column>

              {isLoading && (
                <Center flex={1}>
                  <CustomSpinner isLoading size={32} />
                </Center>
              )}

              {!isLoading && (
                <>
                  <Tabs
                    initialTab={activeTab}
                    onChangeTab={onTabChange}
                    style={{ flex: 1 }}
                    tabListStyle={{
                      marginBottom: 0,
                    }}
                    tabPanelStyle={{
                      display: 'flex',
                      flex: 1,
                    }}
                  >
                    {/* @ts-expect-error -- TabList still written in JavaScript */}
                    <TabList>
                      <Tab
                        title="Favorites"
                        totalCount={favoriteProfessionals?.length}
                        textStyle={{
                          fontSize: 20,
                        }}
                      />
                      <Tab
                        title="Worked With"
                        totalCount={recentProfessionals?.length}
                        textStyle={{
                          fontSize: 20,
                        }}
                      />
                      <Tab
                        title="New to You"
                        totalCount={recommendedProfessionals?.length}
                        textStyle={{
                          fontSize: 20,
                        }}
                        isVisible={isRecommendedProsEnabled}
                      />
                    </TabList>
                    <TabPanel>
                      <FavoriteTabPanel
                        isLoading={isLoading}
                        selectedProfessionals={selectedProfessionals}
                        onSelectProfessionals={onSelectProfessionals}
                        profession={profession}
                        data={favoriteProfessionals ?? []}
                      />
                      <RecentProfessionalTabPanel
                        isLoading={isLoading}
                        selectedProfessionals={selectedProfessionals}
                        onSelectProfessionals={onSelectProfessionals}
                        profession={profession}
                        data={recentProfessionals ?? []}
                      />
                      <RecommendedProsTabPanel
                        isLoading={isLoading}
                        selectedProfessionals={selectedProfessionals}
                        onSelectProfessionals={onSelectProfessionals}
                        profession={profession}
                        data={recommendedProfessionals}
                      />
                    </TabPanel>
                  </Tabs>
                  <Button
                    text={confirmButtonText}
                    disabled={selectedProfessionals.size === 0}
                    isLoading={isInviting && !isLoading}
                    style={{ marginInline: 'auto', width: 152, marginBottom: 32, marginTop: 24 }}
                    onClick={handleConfirmProfessionals}
                  />
                </>
              )}
            </Container>
          </ClickAwayListener>
        </Overlay>
      ) : (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <AnimatedCheckCircle />
            </div>
          )}
          title="Invite(s) Successfully Sent!"
          description="This shift request has been successfully sent to your selected professionals."
          rightButtonAction={onClosePopup}
          rightButtonText="Close"
        />
      )}
    </>
  );
};
